"use client"

import { useState, useEffect } from "react"
import { 
  Building, Plus, Edit3, Trash2, X, Save, Search, Phone, Mail, AlertCircle
} from 'lucide-react'
import { authFetch } from "../utils/authFetch";

const GestionProveedores = ({ API_URL, onClose, token }) => {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  const [formData, setFormData] = useState({ nombre: "", telefono: "", email: "" });

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  useEffect(() => {
    if (token) loadProveedores();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadProveedores = async () => {
    try {
      setLoading(true);
      const response = await authFetch(`${API_URL}/proveedores`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) setProveedores(result.data);
    } catch (err) {
      console.error('Error loading proveedores:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (proveedor = null) => {
    if (proveedor) {
      setEditingProveedor(proveedor);
      setFormData({ nombre: proveedor.nombre, telefono: proveedor.telefono || "", email: proveedor.email || "" });
    } else {
      setEditingProveedor(null);
      setFormData({ nombre: "", telefono: "", email: "" });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProveedor(null);
    setFormData({ nombre: "", telefono: "", email: "" });
  };

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      alert('El nombre del proveedor es requerido');
      return;
    }
    try {
      const url = editingProveedor ? `${API_URL}/proveedores/${editingProveedor.id}` : `${API_URL}/proveedores`;
      const method = editingProveedor ? 'PUT' : 'POST';
      const response = await authFetch(url, { method, headers: authHeaders, body: JSON.stringify(formData) });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        alert(editingProveedor ? '✅ Proveedor actualizado correctamente' : '✅ Proveedor creado correctamente');
        await loadProveedores();
        handleCloseModal();
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error saving proveedor:', err);
      alert('❌ Error al guardar proveedor');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await authFetch(`${API_URL}/proveedores/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        alert('✅ Proveedor eliminado correctamente');
        await loadProveedores();
        setShowDeleteConfirm(null);
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error deleting proveedor:', err);
      alert('❌ Error al eliminar proveedor');
    }
  };

  const filteredProveedores = proveedores.filter(p =>
    p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.email && p.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="gestion-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando proveedores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gestion-container">
      <div className="gestion-header">
        <div className="header-left">
          <Building size={24} className="header-icon" />
          <div>
            <h2>Gestión de Proveedores</h2>
            <p>{proveedores.length} proveedor{proveedores.length !== 1 ? 'es' : ''}</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-close" onClick={onClose}><X size={18} /></button>
        </div>
      </div>

      <div className="gestion-toolbar">
        <div className="search-box">
          <Search size={16} />
          <input type="text" placeholder="Buscar por nombre o email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <button className="btn-primary" onClick={() => handleOpenModal()}>
          <Plus size={16} /><span>Nuevo Proveedor</span>
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Teléfono</th>
              <th>Email</th>
              <th>Productos</th>
              <th width="100">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredProveedores.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  <AlertCircle size={20} />
                  <span>No se encontraron proveedores</span>
                </td>
              </tr>
            ) : (
              filteredProveedores.map((proveedor) => (
                <tr key={proveedor.id}>
                  <td className="font-medium">{proveedor.nombre}</td>
                  <td>
                    {proveedor.telefono ? (
                      <div className="flex-cell"><Phone size={14} /><span>{proveedor.telefono}</span></div>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {proveedor.email ? (
                      <div className="flex-cell"><Mail size={14} /><span>{proveedor.email}</span></div>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td><span className="badge">{proveedor.producto_count || 0}</span></td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon edit" onClick={() => handleOpenModal(proveedor)} title="Editar"><Edit3 size={14} /></button>
                      <button className="btn-icon delete" onClick={() => setShowDeleteConfirm(proveedor.id)} title="Eliminar"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h3>
              <button onClick={handleCloseModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nombre <span className="required">*</span></label>
                <input type="text" placeholder="Nombre del proveedor" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} className="input" autoFocus />
              </div>
              <div className="form-group">
                <label>Teléfono</label>
                <input type="tel" placeholder="Teléfono de contacto" value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} className="input" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" placeholder="Email de contacto" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="input" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseModal}>Cancelar</button>
              <button className="btn-primary" onClick={handleSave} disabled={!formData.nombre.trim()}>
                <Save size={16} /><span>{editingProveedor ? 'Actualizar' : 'Crear'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-box confirm" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon"><AlertCircle size={48} /></div>
            <h3>¿Eliminar Proveedor?</h3>
            <p>Esta acción no se puede deshacer. Los productos asociados permanecerán pero sin proveedor asignado.</p>
            <div className="confirm-actions">
              <button className="btn-secondary" onClick={() => setShowDeleteConfirm(null)}>Cancelar</button>
              <button className="btn-danger" onClick={() => handleDelete(showDeleteConfirm)}>
                <Trash2 size={16} /><span>Eliminar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .gestion-container { display: flex; flex-direction: column; height: 100%; background: white; }
        .loading-state { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 400px; gap: 16px; }
        .spinner { width: 40px; height: 40px; border: 3px solid #E5E7EB; border-top: 3px solid #3B82F6; border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .gestion-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #E5E7EB; }
        .header-left { display: flex; align-items: center; gap: 12px; }
        .header-icon { color: #3B82F6; }
        .gestion-header h2 { margin: 0; font-size: 20px; font-weight: 600; color: #1A1A1A; }
        .gestion-header p { margin: 2px 0 0 0; font-size: 13px; color: #64748B; }
        .header-actions { display: flex; gap: 8px; }
        .btn-close { padding: 6px; border: none; border-radius: 6px; background: #F1F5F9; color: #64748B; cursor: pointer; transition: all 0.2s; }
        .btn-close:hover { background: #E5E7EB; color: #EF4444; }
        .gestion-toolbar { display: flex; justify-content: space-between; align-items: center; padding: 16px 24px; border-bottom: 1px solid #E5E7EB; }
        .search-box { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 6px; width: 300px; }
        .search-box svg { color: #94A3B8; flex-shrink: 0; }
        .search-box input { flex: 1; border: none; background: none; font-size: 13px; color: #1A1A1A; outline: none; }
        .search-box input::placeholder { color: #94A3B8; }
        .btn-primary { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border: none; border-radius: 6px; background: #3B82F6; color: white; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
        .btn-primary:hover { background: #2563EB; }
        .btn-primary:disabled { background: #CBD5E1; cursor: not-allowed; }
        .table-container { flex: 1; overflow: auto; }
        .data-table { width: 100%; border-collapse: collapse; }
        .data-table thead { position: sticky; top: 0; background: #F9FAFB; z-index: 10; }
        .data-table th { padding: 12px 24px; text-align: left; font-size: 12px; font-weight: 600; color: #64748B; text-transform: uppercase; border-bottom: 1px solid #E5E7EB; }
        .data-table td { padding: 14px 24px; font-size: 13px; color: #1A1A1A; border-bottom: 1px solid #F1F5F9; }
        .data-table tbody tr:hover { background: #F9FAFB; }
        .font-medium { font-weight: 500; }
        .flex-cell { display: flex; align-items: center; gap: 6px; }
        .flex-cell svg { color: #94A3B8; }
        .text-muted { color: #94A3B8; }
        .badge { display: inline-flex; align-items: center; justify-content: center; padding: 2px 8px; background: #EFF6FF; color: #3B82F6; font-size: 11px; font-weight: 600; border-radius: 10px; }
        .action-buttons { display: flex; gap: 4px; }
        .btn-icon { padding: 6px; border: none; border-radius: 4px; background: transparent; cursor: pointer; transition: all 0.2s; }
        .btn-icon.edit { color: #3B82F6; }
        .btn-icon.edit:hover { background: #EFF6FF; }
        .btn-icon.delete { color: #EF4444; }
        .btn-icon.delete:hover { background: #FEF2F2; }
        .empty-state { text-align: center; padding: 48px 24px !important; color: #94A3B8; }
        .empty-state svg { margin-bottom: 8px; }
        .empty-state span { display: block; font-size: 13px; }
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-box { background: white; border-radius: 8px; width: 100%; max-width: 480px; overflow: hidden; }
        .modal-box.confirm { max-width: 400px; text-align: center; }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 18px 20px; border-bottom: 1px solid #E5E7EB; }
        .modal-header h3 { margin: 0; font-size: 17px; font-weight: 600; }
        .modal-header button { padding: 5px; border: none; border-radius: 4px; background: none; color: #64748B; cursor: pointer; transition: all 0.2s; }
        .modal-header button:hover { background: #F1F5F9; color: #EF4444; }
        .modal-body { padding: 20px; display: flex; flex-direction: column; gap: 14px; }
        .form-group { display: flex; flex-direction: column; gap: 5px; }
        .form-group label { font-size: 12px; font-weight: 500; color: #64748B; }
        .required { color: #EF4444; }
        .input { padding: 8px 10px; border: 1px solid #E5E7EB; border-radius: 6px; font-size: 13px; color: #1A1A1A; background: white; transition: all 0.2s; }
        .input:focus { outline: none; border-color: #3B82F6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
        .modal-footer { padding: 14px 20px; border-top: 1px solid #E5E7EB; display: flex; justify-content: flex-end; gap: 8px; }
        .btn-secondary { padding: 7px 14px; border: 1px solid #E5E7EB; border-radius: 6px; background: white; color: #64748B; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
        .btn-secondary:hover { background: #F9FAFB; }
        .confirm-icon { display: flex; justify-content: center; padding: 20px; color: #EF4444; }
        .modal-box.confirm h3 { margin: 0 0 8px 0; font-size: 18px; font-weight: 600; color: #1A1A1A; padding: 0 20px; }
        .modal-box.confirm p { margin: 0; padding: 0 20px 20px; font-size: 13px; color: #64748B; line-height: 1.5; }
        .confirm-actions { display: flex; gap: 8px; padding: 14px 20px; border-top: 1px solid #E5E7EB; justify-content: center; }
        .btn-danger { display: flex; align-items: center; gap: 6px; padding: 7px 14px; border: none; border-radius: 6px; background: #EF4444; color: white; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
        .btn-danger:hover { background: #DC2626; }
        @media (max-width: 768px) { .gestion-toolbar { flex-direction: column; gap: 12px; } .search-box { width: 100%; } }
      `}</style>
    </div>
  );
};

export default GestionProveedores;